import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import type { LiabilitiesData, CurrentLoan, CreditCard } from "@/types/fact-finder";

interface LiabilitiesSectionProps {
  data: LiabilitiesData | null;
  hasApplicant2: boolean;
  onSave: (data: LiabilitiesData) => Promise<void>;
  onNext: () => void;
}

const defaultData: LiabilitiesData = {
  loans: [],
  creditCards: [],
};

const defaultLoan: CurrentLoan = {
  loanType: "",
  lender: "",
  amountOwing: 0,
  repaymentAmount: 0,
  repaymentFrequency: "monthly",
  heldBy: "applicant1",
};

const defaultCreditCard: CreditCard = {
  cardType: "",
  lender: "",
  limit: 0,
  amountOwing: 0,
  heldBy: "applicant1",
};

const loanTypes = [
  "Personal Loan",
  "Car Loan",
  "HECS/HELP Debt",
  "Tax Debt",
  "Line of Credit",
  "Overdraft",
  "Buy Now Pay Later",
  "Other",
];

export function LiabilitiesSection({ data, hasApplicant2, onSave, onNext }: LiabilitiesSectionProps) {
  const [formData, setFormData] = useState<LiabilitiesData>(data || defaultData);
  const [isSaving, setIsSaving] = useState(false);

  const formatCurrency = (value: number | undefined): string => {
    if (!value) return "";
    return value.toLocaleString();
  };

  const parseCurrency = (value: string): number => {
    return parseInt(value.replace(/,/g, ""), 10) || 0;
  };

  // Loan helpers
  const addLoan = () =>
    setFormData((prev) => ({ ...prev, loans: [...prev.loans, { ...defaultLoan }] }));
  const removeLoan = (index: number) =>
    setFormData((prev) => ({ ...prev, loans: prev.loans.filter((_, i) => i !== index) }));
  const updateLoan = (index: number, field: string, value: unknown) =>
    setFormData((prev) => ({
      ...prev,
      loans: prev.loans.map((l, i) => (i === index ? { ...l, [field]: value } : l)),
    }));

  // Credit card helpers
  const addCreditCard = () =>
    setFormData((prev) => ({ ...prev, creditCards: [...prev.creditCards, { ...defaultCreditCard }] }));
  const removeCreditCard = (index: number) =>
    setFormData((prev) => ({ ...prev, creditCards: prev.creditCards.filter((_, i) => i !== index) }));
  const updateCreditCard = (index: number, field: string, value: unknown) =>
    setFormData((prev) => ({
      ...prev,
      creditCards: prev.creditCards.map((c, i) => (i === index ? { ...c, [field]: value } : c)),
    }));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(formData);
      onNext();
    } finally {
      setIsSaving(false);
    }
  };

  const ownerOptions = hasApplicant2
    ? [
        { value: "applicant1", label: "Applicant 1" },
        { value: "applicant2", label: "Applicant 2" },
        { value: "joint", label: "Joint" },
      ]
    : [{ value: "applicant1", label: "Applicant 1" }];

  // Calculate totals
  const totalLoansOwing = formData.loans.reduce((sum, l) => sum + l.amountOwing, 0);
  const totalLoanRepayments = formData.loans.reduce((sum, l) => sum + l.repaymentAmount, 0);
  const totalCardLimit = formData.creditCards.reduce((sum, c) => sum + c.limit, 0);
  const totalCardOwing = formData.creditCards.reduce((sum, c) => sum + c.amountOwing, 0);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-serif">Liabilities</h2>
        <p className="text-muted-foreground mt-1">
          List all current loans and credit cards. Property loans should have been included in the Assets section.
        </p>
      </div>

      {/* Current Loans */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Current Loans</h3>
          <Button variant="outline" onClick={addLoan} className="gap-2">
            <FiPlus className="size-4" />
            Add Loan
          </Button>
        </div>

        {formData.loans.length === 0 ? (
          <p className="text-sm text-muted-foreground p-4 bg-muted/30 rounded-lg">
            No loans added. Click "Add Loan" to add personal loans, car loans, HECS debt, etc.
          </p>
        ) : (
          <>
            {formData.loans.map((loan, idx) => (
              <div key={idx} className="p-4 bg-muted/30 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Loan {idx + 1}</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeLoan(idx)}
                    className="text-destructive hover:text-destructive"
                  >
                    <FiTrash2 className="size-4" />
                  </Button>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Loan Type</label>
                    <Select
                      value={loan.loanType}
                      onValueChange={(v) => updateLoan(idx, "loanType", v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {loanTypes.map((type) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Lender</label>
                    <Input
                      value={loan.lender}
                      onChange={(e) => updateLoan(idx, "lender", e.target.value)}
                      placeholder="e.g. CBA, Afterpay"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Held By</label>
                    <Select
                      value={loan.heldBy}
                      onValueChange={(v) => updateLoan(idx, "heldBy", v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ownerOptions.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Amount Owing ($)</label>
                    <Input
                      value={formatCurrency(loan.amountOwing)}
                      onChange={(e) => updateLoan(idx, "amountOwing", parseCurrency(e.target.value))}
                      placeholder="0"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Repayment Amount ($)</label>
                    <Input
                      value={formatCurrency(loan.repaymentAmount)}
                      onChange={(e) => updateLoan(idx, "repaymentAmount", parseCurrency(e.target.value))}
                      placeholder="0"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Repayment Frequency</label>
                    <Select
                      value={loan.repaymentFrequency}
                      onValueChange={(v) => updateLoan(idx, "repaymentFrequency", v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="fortnightly">Fortnightly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}

            {/* Loans Summary */}
            <div className="flex justify-end gap-8 p-4 bg-primary/5 rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">Total Owing</p>
                <p className="text-lg font-semibold">${formatCurrency(totalLoansOwing)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Repayments</p>
                <p className="text-lg font-semibold">${formatCurrency(totalLoanRepayments)}/mo</p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Credit Cards */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Credit Cards</h3>
          <Button variant="outline" onClick={addCreditCard} className="gap-2">
            <FiPlus className="size-4" />
            Add Credit Card
          </Button>
        </div>

        {formData.creditCards.length === 0 ? (
          <p className="text-sm text-muted-foreground p-4 bg-muted/30 rounded-lg">
            No credit cards added. Click "Add Credit Card" to add any credit cards.
          </p>
        ) : (
          <>
            {formData.creditCards.map((card, idx) => (
              <div key={idx} className="p-4 bg-muted/30 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Credit Card {idx + 1}</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeCreditCard(idx)}
                    className="text-destructive hover:text-destructive"
                  >
                    <FiTrash2 className="size-4" />
                  </Button>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Card Type</label>
                    <Input
                      value={card.cardType}
                      onChange={(e) => updateCreditCard(idx, "cardType", e.target.value)}
                      placeholder="e.g. Visa, Mastercard"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Provider/Lender</label>
                    <Input
                      value={card.lender}
                      onChange={(e) => updateCreditCard(idx, "lender", e.target.value)}
                      placeholder="e.g. CBA, ANZ"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Held By</label>
                    <Select
                      value={card.heldBy}
                      onValueChange={(v) => updateCreditCard(idx, "heldBy", v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ownerOptions.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Credit Limit ($)</label>
                    <Input
                      value={formatCurrency(card.limit)}
                      onChange={(e) => updateCreditCard(idx, "limit", parseCurrency(e.target.value))}
                      placeholder="0"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Amount Owing ($)</label>
                    <Input
                      value={formatCurrency(card.amountOwing)}
                      onChange={(e) => updateCreditCard(idx, "amountOwing", parseCurrency(e.target.value))}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            ))}

            {/* Credit Card Summary */}
            <div className="flex justify-end gap-8 p-4 bg-primary/5 rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">Total Limit</p>
                <p className="text-lg font-semibold">${formatCurrency(totalCardLimit)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Owing</p>
                <p className="text-lg font-semibold">${formatCurrency(totalCardOwing)}</p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Important Note */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Lenders assess your serviceability based on the credit limit of your credit cards,
          not the amount owing. Even if you pay your card off in full each month, the limit is still considered.
        </p>
      </div>

      {/* Actions */}
      <div className="flex justify-end pt-4 border-t">
        <Button onClick={handleSave} disabled={isSaving} size="lg">
          {isSaving ? "Saving..." : "Save & Continue"}
        </Button>
      </div>
    </div>
  );
}
